import type { CurrencyCode, IsoDate, IsoDateTime } from './common';

export const CHARGE_TYPES = ['handling', 'shipping', 'receiving', 'storage', 'pallet_storage', 'manual', 'returns', 'packaging'] as const;
export type ChargeType = (typeof CHARGE_TYPES)[number];

export const CHARGE_TYPE_LABELS: Readonly<Record<ChargeType, string>> = {
  handling: 'Elleçleme (toplama & paketleme)',
  shipping: 'Kargo',
  receiving: 'Mal kabul',
  storage: 'Depolama',
  pallet_storage: 'Palet depolama',
  manual: 'Diğer hizmetler',
  returns: 'İade elleçleme',
  packaging: 'Koli (ambalaj)',
};

export const INVOICE_STATUSES = ['draft', 'issued', 'paid', 'cancelled'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];

/**
 * One step of the graduated order fee (kademeli sipariş ücreti): the seller's handled orders of a
 * calendar month are counted in order, and the nth one costs the price of the step n falls in.
 * upTo is the last order number of the step; the last step has null (and every order after).
 */
export interface OrderFeeTier {
  readonly upTo: number | null;
  /** decimal string, 2 decimals */
  readonly price: string;
}

/** Fixed fees next to the desi barem. tenantId null = list price. Decimal strings. */
export interface ServiceTariff {
  readonly id: string;
  readonly tenantId: string | null;
  readonly currency: CurrencyCode;
  readonly validFrom: IsoDate;
  readonly validTo: IsoDate | null;
  /** per packed order; with orderFeeTiers the first step's price (the steps decide) */
  readonly orderFee: string;
  /** graduated order fee by the order's number in the month; empty: orderFee for every order */
  readonly orderFeeTiers: readonly OrderFeeTier[];
  /** per unit after the first one in an order */
  readonly extraUnitFee: string;
  /** per unit received at the dock */
  readonly receivingUnitFee: string;
  /** per desi of stock per day (a tariff that gives storageM3MonthFee has 0 here) */
  readonly storageDesiDayFee: string;
  /** per m³ of stock per month (raf / koli), charged by the day: ÷ 333.33 desi ÷ the month's days; null: storageDesiDayFee */
  readonly storageM3MonthFee: string | null;
  /** per pallet position (a bulk bin the seller has stock in) per month, by the day; 0: bulk stock is charged by desi */
  readonly palletMonthFee: string;
  /** per box the warehouse provides (parcels of an order but those leaving in their own packaging) */
  readonly boxFee: string;
  /** desi assumed for products not measured yet */
  readonly defaultUnitDesi: string;
  /** per customer return received at the desk (İade elleçleme ücreti) */
  readonly returnFee: string;
  readonly vatRate: string;
}

/** Hak ediş: one billable warehouse service. Amounts are decimal strings. */
export interface BillingCharge {
  readonly id: string;
  readonly tenantId: string;
  readonly type: ChargeType;
  readonly description: string;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly amount: string;
  readonly currency: CurrencyCode;
  readonly desi: string | null;
  readonly orderId: string | null;
  readonly shipmentId: string | null;
  readonly serviceDate: IsoDate;
  readonly invoiceId: string | null;
  readonly createdAt: IsoDateTime;
  /** how a storage or pallet storage day was worked out (0067); null for other charges and older days */
  readonly detail: StorageDetail | null;
}

/**
 * How a day's storage charge was worked out (billing_charges.detail; see storage-space.ts). Desi and
 * pallet quantities are decimal strings with 3 decimals.
 */
export interface StorageDetail {
  /**
   * the tariff charges unmeasured goods by where they are kept (₺ / m³ / ay); false (₺ / desi / gün):
   * by the desi per unit wherever they are. Missing in a detail written before it: read as true.
   */
  readonly byLocation: boolean;
  /** measured products: their own desi */
  readonly measured: { readonly units: number; readonly desi: string } | null;
  /**
   * unmeasured products counted by the tariff's desi per unit: not in a shelf or pallet bin with a
   * volume (or on a tariff per desi per day); `full`: bins the measured goods fill on paper
   */
  readonly assumed: { readonly units: number; readonly unitDesi: string; readonly desi: string; readonly full: readonly string[] } | null;
  /** unmeasured products by where they are kept: the seller's share of each bin */
  readonly bins: readonly StorageBinShare[];
  /** pallet bins: the seller's share of each */
  readonly pallets: readonly StoragePalletShare[];
}

/** Unmeasured goods in a bin with a volume: what the measured goods leave free, shared by units. */
export interface StorageBinShare {
  readonly code: string;
  /** the bin's volume in desi (cm³ ÷ 3000) */
  readonly binDesi: string;
  /** the measured goods in the bin, every seller's */
  readonly measuredDesi: string;
  /** units of unmeasured products in the bin, every seller's */
  readonly unmeasuredUnits: number;
  /** the seller's unmeasured units there and the desi they come to */
  readonly units: number;
  readonly desi: string;
}

/** A pallet bin: the seller's share of the pallet by the space its goods take there. */
export interface StoragePalletShare {
  readonly code: string;
  readonly units: number;
  /** "1.000" alone in the bin */
  readonly share: string;
  /** sellers with stock in the bin */
  readonly sellers: number;
}

export interface ServiceInvoiceLine {
  readonly lineNo: number;
  readonly type: ChargeType;
  readonly description: string;
  readonly quantity: string;
  readonly amount: string;
}

/** Depolama & Elleçleme Hizmet Faturası for one seller and period. */
export interface ServiceInvoice {
  readonly id: string;
  readonly tenantId: string;
  readonly invoiceNumber: string;
  readonly periodStart: IsoDate;
  readonly periodEnd: IsoDate;
  readonly status: InvoiceStatus;
  readonly currency: CurrencyCode;
  readonly subtotal: string;
  readonly vatRate: string;
  readonly vatAmount: string;
  readonly total: string;
  readonly issuedAt: IsoDateTime | null;
  readonly lines: readonly ServiceInvoiceLine[];
}
